import React, { useState, useCallback } from "react";
import {
  Box,
  Container,
  Paper,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Link as MuiLink
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FooterCommonLogin from "./footerCommonLogin";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@store/store";
import {
  getPublicUrlThunk,
  getStateListThunk
} from "../redux/thunk/publiccommentsThunk";
import type { StateOption } from "../services/apiEndpoints";
import { getPublicCommentsThunk } from "@modules/login/redux/thunk/loginThunk";
import { openFileBySignedUrl } from "../../../utils/getViewSignedUrlHandler";
import { getFileSignedURLThunk } from "@modules/stepper/redux/thunk/documentThunk";
import { getFileSignedURLMorthThunk } from "@modules/stepper-row/redux/thunk/documentThunk";

type Row = {
  id: string | number;
  state: string;
  subject: React.ReactNode;
  issueDate: string;
  pdf?: {
    href: string;
    label: string;
    onClick?: (e: React.MouseEvent) => void;
  };
};

const PublicComments = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [search, setSearch] = React.useState("");
  const [stateFilter, setStateFilter] = React.useState("All States");
  const [page, setPage] = React.useState(0); // MUI is 0-based
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [rows, setRows] = React.useState<Row[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [stateOptions, setStateOptions] = useState<StateOption[]>([]);

  const states = React.useMemo(
    () => ["All States", ...stateOptions.map(o => o.label)],
    [stateOptions]
  );

  const joinLocation = (d: any) => {
    const parts = [d.address_1, d.address_2, d.village, d.district]
      .map((x: string) => (x || "").trim())
      .filter(Boolean);
    if (d.state) {
      const st = String(d.state).trim();
      if (st && !parts.includes(st)) parts.push(st);
    }
    return parts.length ? parts.join(", ") : "-";
  };

  const buildSubject = (d: any) => {
    const application_code = d.application_code || "-";
    const business = d.business_name || "-";
    const utilityName = d.amenity_name || "-";
    const utilityType = d.utility_type || "-";
    const start = d.start_chainage || "-";
    const end = d.end_chainage || "-";
    const location = joinLocation(d);
    const authority = d.morth_authority || "-";
    const highwayCat = d.highway_category || "-";
    const highwayNum = d.national_highway_number || "-";
    return (
      <>
        Invitation for Public Comments on Right of Way (ROW) Application{" "}
        <strong>{application_code}</strong> for Grant of No Objection
        Certificate (NOC) submitted by <strong>{business}</strong> for{" "}
        <strong>{utilityName}</strong> (<strong>{utilityType}</strong>),
        proposed from Chainage <strong>{start}</strong> to{" "}
        <strong>{end}</strong> at <strong>{location}</strong>, under the
        jurisdiction of <strong>{authority}</strong> on{" "}
        <strong>{highwayCat}</strong> – <strong>{highwayNum}</strong>.
      </>
    );
  };

  const getStateList = async () => {
    try {
      const res = await dispatch(getStateListThunk()).unwrap();
      setStateOptions(res);
    } catch (error: any) {
      console.error("Error fetching state list:", error);
    }
  };

  React.useEffect(() => {
    getStateList();
  }, []);

  const fetchList = React.useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      try {
        const body = {
          searchTerm: search || "",
          state: stateFilter === "All States" ? "" : stateFilter,
          page: page + 1, // API is 1-based
          limit: rowsPerPage
        };

        // const res = await fetch(API_URL, {
        //   method: "POST",
        //   headers: { "Content-Type": "application/json" },
        //   body: JSON.stringify(body),
        //   signal
        // });
        // if (!res.ok) {
        //   throw new Error(`Request failed: ${res.status}`);
        // }
        const res = await dispatch(getPublicCommentsThunk(body)).unwrap();
        console.log("API response:", res);
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        const mapped: Row[] = list.map((d: any, idx: number) => ({
          id: d.application_code ?? idx,
          state: String(d.state ?? ""),
          subject: buildSubject(d),
          issueDate: String(d.issue_date ?? ""),
          pdf: d.demand_draft_url
            ? {
                // Use a dummy href and handle click via onClick
                href: "#",
                label: "Download Pdf",
                onClick: (e: React.MouseEvent) => {
                  e.preventDefault();
                  handleViewFile(d.demand_draft_url);
                }
              }
            : undefined
        }));

        setRows(mapped);
        setTotal(Number(res?.data?.total ?? mapped.length));
      } catch (e: any) {
        if (e?.name !== "AbortError") {
          console.error("Fetch error:", e);
          setRows([]);
          setTotal(0);
        }
      } finally {
        setLoading(false);
      }
    },
    [search, stateFilter, page, rowsPerPage]
  );

  const handleViewFile = useCallback(
    async (doc_name: any) => {
      if (!doc_name) return;
      try {
        const payload: any = await dispatch(
          getPublicUrlThunk({
            document_name: doc_name,
            moduletype: "row"
          }) as any
        ).unwrap();

        if (!payload) return;

        // If API returned a public URL string, open/download it
        if (typeof payload === "string") {
          const a = document.createElement("a");
          a.href = payload;
          a.download = doc_name?.split("-").pop() || "file";
          document.body.appendChild(a);
          a.click();
          a.remove();
          return;
        }

        // If payload is a Blob, trigger a download
        const blob: Blob = payload as Blob;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = doc_name?.split("-").pop() || "file";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Download failed", err);
      }
    },
    [dispatch]
  );

  React.useEffect(() => {
    // Reset to first page when filters change
    setPage(0);
  }, [search, stateFilter, rowsPerPage]);

  React.useEffect(() => {
    const ctrl = new AbortController();
    fetchList(ctrl.signal);
    return () => ctrl.abort();
  }, [fetchList]);

  const formatDate = (iso: string) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "-" : d.toLocaleDateString("en-GB");
  };

  return (
    <>
      <Box
        sx={{
          backgroundImage: `linear-gradient(rgba(15,23,42,0.55), rgba(15,23,42,0.10)), url(${require("@assets/images/common-login-bg.png")})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          minHeight: { xs: 120, md: 140 },
          display: "flex",
          alignItems: "center"
        }}
      >
        <Container
          maxWidth={false}
          sx={{ width: { xs: "95%", md: "95%" }, py: { xs: 4, md: 6 } }}
        >
          <Typography
            variant="h3"
            sx={{
              color: "#FFF",
              fontWeight: 800,
              fontSize: { xs: 24, md: 28 },
              lineHeight: 1.25
            }}
          >
            Invitation of Public Comments
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Box sx={{ bgcolor: "#FFF" }}>
        <Container
          maxWidth={false}
          sx={{ width: { xs: "95%", md: "95%" }, py: { xs: 4, md: 6 } }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 2,
              bgcolor: "#fff"
            }}
          >
            {/* Header line */}
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: 14, md: 16 },
                color: "#3A1C73",
                mb: 2
              }}
            >
              Invitation of Public comments for Right of Way Permission.
            </Typography>

            {/* Filters */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="flex-end"
              sx={{ mb: 2 }}
            >
              <TextField
                size="small"
                placeholder="Search (e.g. Power)"
                value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
                sx={{ width: { xs: "100%", sm: 220 } }}
              />

              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select
                  value={stateFilter}
                  onChange={e => setStateFilter(e.target.value)}
                  displayEmpty
                  renderValue={v => (v ? (v as string) : "Select State")}
                >
                  {states.map(s => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* Table */}
            <TableContainer>
              <Table
                size="small"
                sx={{
                  borderCollapse: "separate",
                  border: "1px solid #CFDAE7",
                  borderRadius: 1
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        bgcolor: "#F9FAFB",
                        color: "#374151",
                        fontWeight: 700,
                        borderBottom: "1px solid #E5E7EB"
                      }
                    }}
                  >
                    <TableCell sx={{ width: 72 }}>S. No</TableCell>
                    <TableCell sx={{ width: 180 }}>State</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell sx={{ width: 160 }}>Issue Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, idx) => (
                    <TableRow
                      key={row.id}
                      sx={{
                        "& td": {
                          borderBottom: "1px solid #E5E7EB",
                          verticalAlign: "top"
                        }
                      }}
                    >
                      <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                      <TableCell>{row.state}</TableCell>
                      <TableCell>
                        <Typography
                          component="span"
                          sx={{ color: "#111827", lineHeight: 1.6 }}
                        >
                          {row.subject}{" "}
                          {row.pdf && (
                            <MuiLink
                              href={row.pdf.href}
                              underline="hover"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                ml: 1
                              }}
                              target="_blank"
                              rel="noopener"
                              aria-label="Download PDF"
                              title="Download PDF"
                              onClick={row.pdf.onClick} // <-- Add this line
                            >
                              <PictureAsPdfIcon
                                sx={{
                                  color: "#d32f2f"
                                }}
                              />
                            </MuiLink>
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(row.issueDate)}</TableCell>
                    </TableRow>
                  ))}

                  {!loading && rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                        No records found.
                      </TableCell>
                    </TableRow>
                  )}

                  {loading && rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                        Loading...
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e =>
                setRowsPerPage(parseInt(e.target.value, 10))
              }
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Rows Per Page"
            />
          </Paper>
        </Container>
      </Box>

      <FooterCommonLogin />
    </>
  );
};

export default PublicComments;
